import * as React from 'react';
import { isNil } from 'lodash';
import classnames from 'classnames';

import Popover from 'components/popover/Popover';

import { __t, applyTranslations } from 'utils/language';

import type { MetricsBarItem } from 'types';

import './metrics-bar.scss';

applyTranslations(require('./metrics-bar.i18n.json'));

type Props = {
  classNamePopover?: string,
  metrics: Array<MetricsBarItem>,
  metricsType?: string | null,
  isMonochrome?: boolean,
  padding?: 'normal' | 'small'
};

const MetricComponent = ({
  name,
  value,
  industryBenchmark,
  industryBenchmarkLabel
}: Partial<MetricsBarItem>) => (
  <div className="metrics-bar__metric-container">
    <div className="metrics-bar__metric">
      {!isNil(value) && (
        <div className="metrics-bar__metric-value">{value}</div>
      )}
      {!isNil(name) && (
        <div className="metrics-bar__metric-name">{name}</div>
      )}
    </div>
    {
      industryBenchmark && (
        <div className="metrics-bar__metric metrics-bar__metric-benchmark">
          <div className="metrics-bar__benchmark-seperator" />
          <div className="metrics-bar__benchmark-metric-value">
            {industryBenchmark}
          </div>
          <div className="metrics-bar__benchmark-metric-name">
            {industryBenchmarkLabel ?? __t('metrics_bar.industry_benchmark_label')}
          </div>
        </div>
      )
    }
  </div>
);

const MetricsBar = (
  {
    classNamePopover,
    padding = 'normal',
    metrics,
    metricsType,
    isMonochrome
  }: Props
): JSX.Element => {
  const classNames = classnames('metrics-bar', {
    'metrics-bar--monochrome': isMonochrome
  });
  const itemClassNames = classnames('metrics-bar__separated-item', {
    [`metrics-bar__separated-item--padding-${padding}`]: padding
  });

  return (
    <div className={classNames}>
      {
        metricsType && (
          <div className="metrics-bar__type">
            {metricsType}
          </div>
        )
      }
      <div className="metrics-bar__container">
        {metrics.map(({ key, name, value, popover, industryBenchmark, industryBenchmarkLabel }) => {
          // popover for whole section, for label-only tooltip it's possible to pass name as React.Component
          if (popover) {
            return (
              <Popover
                key={key}
                classNamePopover={classNamePopover}
                classNameWrapper={itemClassNames}
                triggerComponent={(
                  <MetricComponent
                    name={name}
                    value={value}
                    industryBenchmark={industryBenchmark}
                    industryBenchmarkLabel={industryBenchmarkLabel}
                  />
                )}
              >
                {popover}
              </Popover>
            );
          }

          return (
            <div key={key} className={itemClassNames}>
              <MetricComponent
                name={name}
                value={value}
                industryBenchmark={industryBenchmark}
                industryBenchmarkLabel={industryBenchmarkLabel}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MetricsBar;
